import { divide } from "@aprao/common/src/math/divide.js";
import { FRACTION_ZERO } from "@aprao/common/src/math/fraction.js";
import {
  formatDecimalNumber,
  formatPercentage,
} from "@aprao/common/src/numberFormats.js";
import type { AppraisalDocumentFacade } from "@aprao/model/src/stores/AppraisalDocumentFacade.js";
import { BuildPhaseRelatives } from "@aprao/model/src/stores/build/BuildPhaseRelatives.js";

import { useI18n } from "../../common/ApraoI18n.js";
import { useNumberUnits } from "../../common/useNumberUnits.js";
import { buildBuildPhaseName } from "../../i18n/buildBuildPhaseName.js";
import { Section } from "../Section.js";
import { SimpleTableRow, SimpleTableSection } from "../SimpleTableRow.js";

export const BuildCost = (props: { appraisal: AppraisalDocumentFacade }) => {
  const { appraisal } = props;

  const totalCostPerGrossArea = divide(
    appraisal.getBuildCostTotalWithContingency("debtFunded"),
    appraisal.grossAreaTotal,
  );

  const { msg } = useI18n();
  const { currency, lengthUnit } = useNumberUnits();

  return (
    <Section title={msg("global.build")}>
      <SimpleTableSection>
        <SimpleTableRow
          isHeader
          cells={[
            "Preliminary costs",
            null,
            null,
            null,
            null,
            `Total (${currency.symbol})`,
          ]}
        />
        {appraisal.buildCostsPreliminary.costs.map((cost, i) => (
          <SimpleTableRow
            key={i}
            cells={[
              cost.named.name,
              null,
              null,
              null,
              null,
              formatDecimalNumber(cost.valueHolder.value ?? FRACTION_ZERO),
            ]}
          />
        ))}
        <SimpleTableRow
          bold
          borderTop
          borderBottom
          cells={[
            "Total",
            null,
            null,
            null,
            null,
            formatDecimalNumber(
              appraisal.buildCostsPreliminary.totalCost ?? FRACTION_ZERO,
            ),
          ]}
        />
      </SimpleTableSection>

      {appraisal.buildCostsPhases.map((phase, phaseIdx) => {
        const phaseRelatives = new BuildPhaseRelatives(phase);

        return (
          <SimpleTableSection key={phaseIdx}>
            <SimpleTableRow
              isHeader
              cells={[
                `${buildBuildPhaseName(
                  phase.$model,
                  appraisal.$model.stages.build.buildPhases,
                )}${
                  phase.fundingType === "forwardFunded" ?
                    " (forward funded)"
                  : ""
                }`,
                null,
                null,
                `${lengthUnit.symbol}² (${msg("global.gia")})`,
                `${currency.symbol}/${lengthUnit.symbol}²`,
                `Total (${currency.symbol})`,
              ]}
            />
            {phase.costs.map((cost, costIdx) => (
              <SimpleTableRow
                key={costIdx}
                cells={[
                  cost.named.name,
                  null,
                  null,
                  null,
                  formatDecimalNumber(
                    cost.relativesSet.getRelative("ratioGrossArea") ??
                      FRACTION_ZERO,
                  ),
                  formatDecimalNumber(
                    cost.relativesSet.getRelative("absolute") ?? FRACTION_ZERO,
                  ),
                ]}
              />
            ))}
            <SimpleTableRow
              bold
              borderTop
              borderBottom
              cells={[
                "Total (excl contingency)",
                null,
                null,
                formatDecimalNumber(
                  phase.grossArea.relativesSet.getRelative("absolute") ??
                    FRACTION_ZERO,
                ),
                formatDecimalNumber(
                  phaseRelatives.costTotal.relativesSet.getRelative(
                    "ratioGrossArea",
                  ) ?? FRACTION_ZERO,
                ),
                formatDecimalNumber(phase.costTotal ?? FRACTION_ZERO),
              ]}
            />
            <SimpleTableRow
              cells={[
                "Contingency",
                null,
                formatPercentage(
                  phase.contingency.relativesSet.getRelative(
                    "ratioGrossCost",
                  ) ?? FRACTION_ZERO,
                ),
                null,
                null,
                formatDecimalNumber(
                  phase.contingency.relativesSet.getRelative("absolute") ??
                    FRACTION_ZERO,
                ),
              ]}
            />
            <SimpleTableRow
              bold
              borderTop
              borderBottom
              cells={[
                "Total (inc. contingency)",
                null,
                null,
                null,
                formatDecimalNumber(
                  phaseRelatives.costTotalWithContingency.relativesSet.getRelative(
                    "ratioGrossArea",
                  ) ?? FRACTION_ZERO,
                ),
                formatDecimalNumber(
                  phase.costTotalWithContingency ?? FRACTION_ZERO,
                ),
              ]}
            />
          </SimpleTableSection>
        );
      })}
      <SimpleTableSection>
        <SimpleTableRow
          bold
          borderBottom
          borderTop
          thickerBorder
          cells={[
            msg("global.totalBuildCosts"),
            null,
            null,
            formatDecimalNumber(appraisal.grossAreaTotal ?? FRACTION_ZERO),
            formatDecimalNumber(totalCostPerGrossArea ?? FRACTION_ZERO),
            formatDecimalNumber(
              appraisal.getBuildCostTotalWithContingency("any") ??
                FRACTION_ZERO,
            ),
          ]}
        />
      </SimpleTableSection>
    </Section>
  );
};
